import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'designSystem/lib/utils'
import { X } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import * as React from 'react'

const dialogTitleVariants = cva('font-semibold text-foreground', {
  variants: {
    size: {
      default: 'text-lg leading-none',
      sm: 'text-sm leading-5',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const dialogDescriptionVariants = cva('text-muted-foreground', {
  variants: {
    size: {
      default: 'text-sm',
      sm: 'text-sm leading-5',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot='dialog' {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot='dialog-close' {...props} />
}

function DialogOverlay({
  className,
  backdrop = 'dim',
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay> & {
  backdrop?: 'dim' | 'blur'
}) {
  return (
    <DialogPrimitive.Overlay
      data-slot='dialog-overlay'
      className={cn(
        'fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        backdrop === 'blur' && 'backdrop-blur-sm',
        className
      )}
      {...props}
    />
  )
}

const tallDialogContentClassName =
  'data-[layout=tall]:inset-y-2 data-[layout=tall]:flex data-[layout=tall]:w-[min(40rem,calc(100%-2rem))] data-[layout=tall]:max-w-none data-[layout=tall]:max-h-[calc(var(--visual-viewport-height)-1rem)] data-[layout=tall]:translate-y-0 data-[layout=tall]:flex-col data-[layout=tall]:overflow-hidden [&[data-layout=tall]>*]:min-h-0 [&[data-layout=tall]>*]:min-w-0 [&[data-layout=tall]>*]:flex-1'

const fillDialogContentClassName =
  'data-[layout=fill]:flex data-[layout=fill]:flex-col data-[layout=fill]:w-[calc(100%-1rem)] data-[layout=fill]:max-w-none data-[layout=fill]:h-[calc(var(--visual-viewport-height)-1rem)] data-[layout=fill]:overflow-hidden [&[data-layout=fill]>*]:min-h-0 [&[data-layout=fill]>*]:min-w-0'

function DialogContent({
  className,
  backdrop = 'dim',
  size = 'default',
  padding = 'default',
  layout = 'default',
  showCloseButton = true,
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  backdrop?: 'dim' | 'blur'
  size?: 'default' | 'sm' | 'md' | 'lg'
  padding?: 'default' | 'none'
  /**
   * `tall` stretches the card between the viewport's top and bottom insets.
   * `fill` makes the card near-full-viewport on all sides.
   */
  layout?: 'default' | 'tall' | 'fill'
  showCloseButton?: boolean
}) {
  return (
    <DialogPortal>
      <DialogOverlay backdrop={backdrop} />
      <DialogPrimitive.Content
        data-slot='dialog-content'
        data-padding={padding}
        data-size={size}
        data-layout={layout}
        className={cn(
          'fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-background p-6 shadow-lg duration-200 data-[padding=none]:gap-0 data-[padding=none]:p-0 data-[layout=default]:data-[size=sm]:sm:max-w-sm data-[layout=default]:data-[size=md]:sm:max-w-md data-[layout=default]:data-[size=lg]:sm:max-w-2xl data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[layout=default]:data-[size=default]:sm:max-w-lg',
          tallDialogContentClassName,
          fillDialogContentClassName,
          className
        )}
        {...props}>
        {children}
        {showCloseButton ? (
          <DialogPrimitive.Close
            data-slot='dialog-close'
            className='ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0'>
            <X className='size-4' />
            <span className='sr-only'>Close</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-header'
      className={cn(
        'flex flex-col gap-1.5 text-center sm:text-left',
        className
      )}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-footer'
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  size = 'default',
  srOnly = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title> &
  VariantProps<typeof dialogTitleVariants> & {
    /** Name the dialog for assistive tech without a visible heading. */
    srOnly?: boolean
  }) {
  return (
    <DialogPrimitive.Title
      data-slot='dialog-title'
      className={cn(
        dialogTitleVariants({ size }),
        srOnly && 'sr-only',
        className
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  size = 'default',
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description> &
  VariantProps<typeof dialogDescriptionVariants>) {
  return (
    <DialogPrimitive.Description
      data-slot='dialog-description'
      className={cn(dialogDescriptionVariants({ size }), className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
